import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { CalendarDays, ChevronLeft, ChevronRight, Wallet, CreditCard, Loader2 } from "lucide-react";
import { auth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;
const SIXO_PACKS = { pack1: 450, pack2: 650, pack3: 900 };

// paliers d'acompte Stripe selon le budget de la réservation
const depositTier = (budget) => {
    if (budget >= 5000) return { key: "acompte_1500", label: "1 500 CHF" };
    if (budget >= 2000) return { key: "acompte_750", label: "750 CHF" };
    return { key: "acompte_300", label: "300 CHF" };
};
const START = new Date(2026, 10, 1); // 1er novembre 2026
const BLOCKED = new Set(["12-25", "01-01"]); // Noël + Jour de l'an

const DAYS_FR = ["LU", "MA", "ME", "JE", "VE", "SA", "DI"];
const MONTHS_FR = ["JANVIER", "FÉVRIER", "MARS", "AVRIL", "MAI", "JUIN", "JUILLET", "AOÛT", "SEPTEMBRE", "OCTOBRE", "NOVEMBRE", "DÉCEMBRE"];
const MONTHS_EN = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];

const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Booking() {
    const { lang } = useLang();
    const [month, setMonth] = useState(() => new Date(2026, 10, 1));
    const [taken, setTaken] = useState([]);
    const [picked, setPicked] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", instagram: "", project_type: "MUSIC VIDEO", budget: 750, note: "", provider: "gloom" });
    const [done, setDone] = useState(null);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);
    const [paying, setPaying] = useState(false);
    const [payError, setPayError] = useState(null);
    const [me, setMe] = useState(null);

    useEffect(() => {
        auth.fetch(`${API}/api/auth/me`)
            .then((r) => (r.ok ? r.json() : null))
            .then((u) => setMe(u))
            .catch(() => setMe(null));
    }, []);

    const payDeposit = async () => {
        setPaying(true);
        setPayError(null);
        try {
            const tier = done.provider === "6xo" ? { key: `acompte_6xo_${done.pack || "pack1"}`, label: `${SIXO_PACKS[done.pack || "pack1"]} CHF` } : depositTier(done.budget || 750);
            const r = await auth.fetch(`${API}/api/payments/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    lookup_key: tier.key,
                    origin_url: window.location.origin,
                    booking_ref: `${done.date} — ${done.project_type} — ${done.provider}`,
                }),
            });
            const d = await r.json();
            if (!r.ok || !d.checkout_url) throw new Error();
            window.location.href = d.checkout_url;
        } catch {
            setPayError(lang === "fr" ? "PAIEMENT INDISPONIBLE — RÉESSAYEZ PLUS TARD" : "PAYMENT UNAVAILABLE — TRY AGAIN LATER");
            setPaying(false);
        }
    };

    useEffect(() => {
        fetch(`${API}/api/bookings/taken`)
            .then((r) => r.json())
            .then((d) => setTaken(d.taken || []))
            .catch(() => {});
    }, []);

    const cells = useMemo(() => {
        const y = month.getFullYear();
        const m = month.getMonth();
        const first = new Date(y, m, 1);
        const lead = (first.getDay() + 6) % 7; // Monday-first
        const days = new Date(y, m + 1, 0).getDate();
        const out = [];
        for (let i = 0; i < lead; i++) out.push(null);
        for (let d = 1; d <= days; d++) out.push(new Date(y, m, d));
        return out;
    }, [month]);

    const stateOf = (d) => {
        if (!d) return "empty";
        const key = iso(d);
        if (d < START) return "off";
        if (BLOCKED.has(key.slice(5))) return "off";
        if (taken.includes(key)) return "taken";
        return "free";
    };

    const submit = async () => {
        setSending(true);
        setError(null);
        try {
            const r = await fetch(`${API}/api/bookings`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ ...form, date: iso(picked), provider: form.provider || "gloom" }),
            });
            if (!r.ok) {
                const e = await r.json().catch(() => ({}));
                throw new Error(e.detail || "error");
            }
            const doc = await r.json();
            setDone(doc);
            setTaken((t) => [...t, iso(picked)]);
        } catch (e) {
            setError(String(e.message || e));
        } finally {
            setSending(false);
        }
    };

    const gcalLink = (doc) => {
        const d = doc.date.replaceAll("-", "");
        const text = encodeURIComponent(`Gloom Ent. — ${doc.project_type} (${doc.name})`);
        return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${d}/${d}`;
    };

    const months = lang === "fr" ? MONTHS_FR : MONTHS_EN;

    return (
        <section
            id="booking"
            data-testid="booking-section"
            className="relative overflow-hidden bg-ink py-24 text-cream md:py-36"
        >
            <div className="relative mx-auto max-w-[1600px] px-5 md:px-10">
                <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <SectionLabel en={tx(lang, T.booking.label)} fr={tx(lang, T.booking.label)} />
                        <FadeUp delay={0.1}>
                            <h2 className="mt-8 font-display text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                                {tx(lang, T.booking.title)}
                                <span className="text-violet">.</span>
                            </h2>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.2}>
                        <p className="max-w-xs font-mono text-[9px] leading-relaxed tracking-[0.3em] text-cream/40 md:text-[10px]">
                            {tx(lang, T.booking.note)}
                        </p>
                    </FadeUp>
                </div>

                <FadeUp delay={0.1} className="mt-14">
                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
                        {/* calendar */}
                        <div className="border border-white/10 p-6 md:p-8 lg:col-span-7">
                            <div className="flex items-center justify-between">
                                <button
                                    data-testid="booking-prev"
                                    onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                                    disabled={month <= START}
                                    className="flex h-9 w-9 items-center justify-center border border-white/15 text-cream/60 transition-colors hover:border-cream hover:text-cream disabled:opacity-20"
                                    aria-label="Previous month"
                                >
                                    <ChevronLeft size={14} />
                                </button>
                                <p className="font-display text-lg font-extrabold uppercase tracking-wide md:text-xl" data-testid="booking-month">
                                    {months[month.getMonth()]} {month.getFullYear()}
                                </p>
                                <button
                                    data-testid="booking-next"
                                    onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                                    className="flex h-9 w-9 items-center justify-center border border-white/15 text-cream/60 transition-colors hover:border-cream hover:text-cream"
                                    aria-label="Next month"
                                >
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                            <div className="mt-6 grid grid-cols-7 gap-1">
                                {DAYS_FR.map((d) => (
                                    <p key={d} className="pb-2 text-center font-mono text-[8px] tracking-[0.2em] text-cream/35">
                                        {d}
                                    </p>
                                ))}
                                {cells.map((d, i) => {
                                    const st = stateOf(d);
                                    const key = d ? iso(d) : `x${i}`;
                                    const selected = d && picked && iso(d) === iso(picked);
                                    return (
                                        <button
                                            key={key}
                                            data-testid={d ? `booking-day-${key}` : undefined}
                                            disabled={st !== "free"}
                                            onClick={() => {
                                                setPicked(d);
                                                setDone(null);
                                                setError(null);
                                            }}
                                            className={`aspect-square border font-mono text-[10px] transition-all duration-300 md:text-[11px] ${
                                                st === "empty"
                                                    ? "border-transparent"
                                                    : st === "free"
                                                      ? selected
                                                          ? "border-violet bg-violet text-cream"
                                                          : "border-white/10 text-cream/75 hover:border-violet hover:text-cream"
                                                      : st === "taken"
                                                        ? "border-white/5 text-cream/25 line-through"
                                                        : "border-transparent text-cream/15"
                                            }`}
                                        >
                                            {d ? d.getDate() : ""}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-5 flex flex-wrap gap-5 font-mono text-[8px] tracking-[0.25em] text-cream/40">
                                <span className="flex items-center gap-2">
                                    <span className="h-2 w-2 border border-white/25" /> {tx(lang, T.booking.free)}
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="h-2 w-2 bg-violet" /> {tx(lang, T.booking.picked)}
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="h-2 w-2 bg-white/10" /> {tx(lang, T.booking.taken)}
                                </span>
                            </div>
                            <p
                                data-testid="booking-deposit-note"
                                className="mt-5 flex items-start gap-3 border-t border-white/10 pt-5 font-mono text-[9px] leading-relaxed tracking-[0.25em] text-gold/90"
                            >
                                <Wallet size={12} className="mt-0.5 shrink-0" />
                                {tx(lang, T.pricing.deposit)}
                            </p>
                        </div>

                        {/* booking form */}
                        <div className="border border-white/10 p-6 md:p-8 lg:col-span-5">
                            {!picked && (
                                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                                    <CalendarDays size={22} className="text-violet" />
                                    <p className="font-mono text-[10px] leading-relaxed tracking-[0.3em] text-cream/40">
                                        {tx(lang, T.booking.pick)}
                                    </p>
                                </div>
                            )}
                            {picked && !done && (
                                <div>
                                    <p className="font-mono text-[10px] tracking-[0.3em] text-violet" data-testid="booking-picked-date">
                                        {iso(picked)}
                                    </p>
                                    <div className="mt-5 space-y-4">
                                        <input
                                            data-testid="booking-name"
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            placeholder={tx(lang, T.booking.fName)}
                                            className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30 focus:border-violet"
                                        />
                                        <input
                                            data-testid="booking-email"
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            placeholder={tx(lang, T.booking.fEmail)}
                                            className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30 focus:border-violet"
                                        />
                                        <input
                                            data-testid="booking-instagram"
                                            value={form.instagram}
                                            onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                                            placeholder={tx(lang, T.booking.fInsta)}
                                            className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30 focus:border-violet"
                                        />
                                        <select
                                            data-testid="booking-type"
                                            value={form.project_type}
                                            onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                                            className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none focus:border-violet"
                                        >
                                            {["MUSIC VIDEO", "COMMERCIAL", "WEDDING", "EVENT", "PHOTO"].map((o) => (
                                                <option key={o} value={o} className="bg-ink">{o}</option>
                                            ))}
                                        </select>
                                        <div>
                                            <p className="mb-2 font-mono text-[9px] tracking-[0.25em] text-cream/40">
                                                {tx(lang, T.booking.fBudget)} — {form.budget} CHF
                                                {form.budget >= 5000 && (
                                                    <span className="ml-2 text-gold">VIP</span>
                                                )}
                                            </p>
                                            <input
                                                data-testid="booking-budget"
                                                type="range"
                                                min="500"
                                                max="8000"
                                                step="250"
                                                value={form.budget}
                                                onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })}
                                                className="w-full accent-violet"
                                            />
                                        </div>
                                        {error && (
                                            <p data-testid="booking-error" className="font-mono text-[9px] tracking-[0.2em] text-blood">
                                                {error}
                                            </p>
                                        )}
                                        <button
                                            data-testid="booking-submit"
                                            onClick={submit}
                                            disabled={sending || !form.name || !form.email || !form.instagram}
                                            className="w-full border border-violet/60 py-3 font-mono text-[10px] tracking-[0.3em] text-violet transition-all hover:bg-violet hover:text-cream disabled:opacity-30"
                                        >
                                            {sending ? "…" : tx(lang, T.booking.submit)}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {done && (
                                <motion.div
                                    data-testid="booking-success"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex h-full flex-col justify-center"
                                >
                                    <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-cream">
                                        {tx(lang, T.booking.done)}
                                        <span className="text-violet">.</span>
                                    </p>
                                    <p className="mt-3 font-mono text-[10px] leading-relaxed tracking-[0.2em] text-cream/50">
                                        {done.date} — {done.project_type}
                                        {done.vip ? " — VIP" : ""}
                                    </p>
                                    <a
                                        data-testid="booking-gcal"
                                        href={gcalLink(done)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-6 inline-flex items-center gap-2 border border-gold/50 px-5 py-3 font-mono text-[9px] tracking-[0.3em] text-gold transition-all hover:bg-gold hover:text-ink"
                                    >
                                        <CalendarDays size={12} />
                                        {tx(lang, T.booking.gcal)}
                                    </a>

                                    {/* acompte — Stripe (carte) uniquement */}
                                    <div className="mt-8 border-t border-gold/20 pt-6">
                                        <p className="font-mono text-[9px] leading-relaxed tracking-[0.25em] text-gold/90">
                                            {tx(lang, T.booking.depositTitle)} — {depositTier(done.budget).label}
                                        </p>
                                        <div className="mt-4 flex flex-col gap-3">
                                            <button
                                                data-testid="booking-pay-stripe"
                                                onClick={payDeposit}
                                                disabled={paying}
                                                className="inline-flex items-center justify-center gap-2 border border-violet bg-violet/15 px-5 py-3.5 font-mono text-[9px] tracking-[0.3em] text-violet transition-all hover:bg-violet hover:text-cream disabled:opacity-40"
                                            >
                                                {paying ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
                                                {tx(lang, T.booking.payCard)}
                                            </button>
                                        </div>
                                        {payError && (
                                            <p data-testid="booking-pay-error" className="mt-3 font-mono text-[9px] tracking-[0.2em] text-blood">{payError}</p>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}
